import React from 'react';
import PropTypes from 'prop-types';
import { Container } from 'semantic-ui-react';
import TeaserImages from '../Helpers/TeaserImages';
import RelatedItemsDisplay from '../Helpers/RelatedItemsDisplay';
import Sections from '../Helpers/Sections';
import DefaultViewWithoutTitle from '../../Custom/DefaultViewWithoutTitle';
import { hasImage, vocabTitle } from '../../../helpers/content';
import { flattenRichTextToAppURL } from '../../../helpers/richText';
import { formatDate } from '../../../helpers/dates';

const EventView = (props) => {
  const { content } = props;

  const typeLabel = vocabTitle(content.event_type) || null;

  const hasImages =
    hasImage(content.teaser_image1) || hasImage(content.teaser_image2);

  const hasSchedule = content.event_start_date || content.event_end_date;
  const sameDay =
    content.event_start_date &&
    content.event_end_date &&
    content.event_start_date === content.event_end_date;

  const hasVenue =
    content.event_location || content.venue_address || content.event_website;

  const organizers = Array.isArray(content.related_organizers)
    ? content.related_organizers.filter(Boolean)
    : [];
  const hasOrganizers = organizers.length > 0 || content.external_organizers;

  const hasDetails =
    content.event_details?.data && content.event_details.data !== '<p></p>';

  const showPublications =
    !content.hide_related_publications &&
    content.related_publications?.length > 0;

  const relatedTopics = Array.isArray(content.related_topics)
    ? content.related_topics.filter(Boolean)
    : [];
  const hasTopics = relatedTopics.length > 0 && !content.hide_related_topics;

  return (
    <Container id="view-wrapper" className="EventView">
      <div className="view-header">
        {typeLabel && <p className="view-header__subtitle">{typeLabel}</p>}
        <h2 className="view-header__title">{content.title || content.id}</h2>
        {/* No description here. `description` is a listing field - the one
            line that goes on a card and into a search result - and repeating
            it as the first paragraph of the page it summarises says the same
            thing twice. The full view has its own content. */}
        <div className="view-header__divider" />
      </div>
      <Sections>
        {/* Images and schedule read as one section. */}
        {(hasImages || hasSchedule || hasVenue) && (
          <>
            {hasImages && <TeaserImages content={content} />}

            {(hasSchedule || hasVenue) && (
              <div className="view-meta-grid">
                <div className="view-meta-grid__col">
                  {hasSchedule && (
                    <div className="view-section">
                      <h3 className="view-section__label">
                        {sameDay ? 'Date:' : 'Dates:'}
                      </h3>
                      <p className="view-section__value">
                        {sameDay
                          ? formatDate(content.event_start_date)
                          : [
                              formatDate(content.event_start_date),
                              formatDate(content.event_end_date),
                            ]
                              .filter(Boolean)
                              .join(' – ')}
                      </p>
                    </div>
                  )}
                  {content.online_event && (
                    <div className="view-section">
                      <h3 className="view-section__label">Format:</h3>
                      <p className="view-section__value">Online / hybrid</p>
                    </div>
                  )}
                </div>
                <div className="view-meta-grid__col">
                  {content.event_location && (
                    <div className="view-section">
                      <h3 className="view-section__label">Location:</h3>
                      <p className="view-section__value">
                        {content.event_location}
                      </p>
                    </div>
                  )}
                  {content.venue_address && (
                    <div className="view-section">
                      <p className="view-section__value view-section__value--lines">
                        {content.venue_address}
                      </p>
                    </div>
                  )}
                  {content.event_website && (
                    <div className="view-section__buttons">
                      <a
                        href={content.event_website}
                        className="view-section__button"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Event Website
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {hasOrganizers && (
          <>
            <div className="view-section">
              <h3 className="view-section__label">Organizers:</h3>
              {organizers.length > 0 && (
                <RelatedItemsDisplay
                  items={organizers}
                  mode="links"
                  kind="person"
                  fieldKey={`${content['@id']}-related_organizers`}
                />
              )}
              {content.external_organizers && (
                <p className="view-section__value view-section__value--lines">
                  {content.external_organizers}
                </p>
              )}
            </div>
          </>
        )}

        {hasDetails && (
          <>
            <div className="view-section">
              <div
                className="view-section__richtext"
                dangerouslySetInnerHTML={{
                  __html: flattenRichTextToAppURL(content.event_details.data),
                }}
              />
            </div>
          </>
        )}

        {showPublications && (
          <>
            <div className="view-section">
              <h3 className="view-section__label">Related Publications:</h3>
              <RelatedItemsDisplay
                items={content.related_publications}
                mode={content.related_publications_display}
                kind="plain"
                fieldKey={`${content['@id']}-related_publications`}
              />
            </div>
          </>
        )}

        {hasTopics && (
          <>
            <div className="view-section">
              <h3 className="view-section__label">Related Topics:</h3>
              <RelatedItemsDisplay
                items={relatedTopics}
                mode={content.related_topics_display}
                kind="plain"
                fieldKey={`${content['@id']}-related_topics`}
              />
            </div>
          </>
        )}
      </Sections>

      <DefaultViewWithoutTitle {...props} />
    </Container>
  );
};

EventView.propTypes = {
  content: PropTypes.shape({
    title: PropTypes.string,
    id: PropTypes.string,
    description: PropTypes.string,
    event_type: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({ title: PropTypes.string }),
    ]),
    event_start_date: PropTypes.string,
    event_end_date: PropTypes.string,
    event_location: PropTypes.string,
    venue_address: PropTypes.string,
    online_event: PropTypes.bool,
    event_website: PropTypes.string,
    related_organizers: PropTypes.array,
    external_organizers: PropTypes.string,
    event_details: PropTypes.shape({ data: PropTypes.string }),
    teaser_image1: PropTypes.object,
    teaser_image1_caption: PropTypes.string,
    teaser_image1_copyright: PropTypes.string,
    teaser_image1_copyright_black_font: PropTypes.bool,
    teaser_image2: PropTypes.object,
    teaser_image2_caption: PropTypes.string,
    teaser_image2_copyright: PropTypes.string,
    teaser_image2_copyright_black_font: PropTypes.bool,
    hide_related_publications: PropTypes.bool,
    related_publications_display: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({ token: PropTypes.string }),
    ]),
    related_publications: PropTypes.array,
    hide_related_topics: PropTypes.bool,
    related_topics_display: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({ token: PropTypes.string }),
    ]),
    related_topics: PropTypes.array,
  }).isRequired,
};

export default EventView;
